import {
    formatInt,
    formatShare,
    formatSignedPercent,
    getRelativeEdgeChipState,
    getRelativeEdgeDisqualificationLabel,
    type RelativeEdgeChipInput
} from './regimeProfilePresentation';

/**
 * The relative-edge chip. Constraint 6: a verdict badge and this chip are one unit — the chip renders
 * only immediately beside a verdict badge (the Layer 1 header and the Layer 5 comparison legs) and has
 * no standalone mount point; conversely no verdict badge may render without it.
 */
export function RegimeRelativeEdgeChip({ report }: { report: RelativeEdgeChipInput }) {
    const chip = getRelativeEdgeChipState(report);

    if (chip.kind === 'unresolved') {
        return (
            <span className="inline-flex items-center gap-1.5 rounded border border-zinc-700 bg-zinc-800/60 px-2 py-1 text-xs text-zinc-400">
                <span className="font-medium text-zinc-300">Relative edge</span>
                {chip.disqualification === 'mixed_instrument_basis'
                    ? 'unresolved — mixed option/underlying basis'
                    : 'unresolved — no covered baseline bucket'}
            </span>
        );
    }

    return (
        <span className="inline-flex flex-wrap items-center gap-x-2 gap-y-1 rounded border border-zinc-700 bg-zinc-800/60 px-2 py-1 text-xs">
            <span className="font-medium text-zinc-300">Relative edge</span>
            <span className={chip.edgePercent >= 0 ? 'font-semibold text-green-400' : 'font-semibold text-red-400'}>
                {formatSignedPercent(chip.edgePercent)}
            </span>
            <span className="text-zinc-400">
                {formatInt(chip.coveredCount)}/{formatInt(chip.eligibleCount)} ({formatShare(chip.coverage)})
            </span>
            {chip.qualifies ? (
                <span className="font-medium text-green-400">✓ positive benchmark</span>
            ) : (
                <span className="font-medium text-amber-400">
                    ✗{' '}
                    {chip.disqualification !== null
                        ? getRelativeEdgeDisqualificationLabel(chip.disqualification)
                        : 'benchmark inconclusive'}
                </span>
            )}
        </span>
    );
}
